import React, { useMemo, useRef, useState } from 'react';
// EAA-1817: Popover imported directly from antd because it is not yet
// re-exported from the UI kit barrel (src/components/ui/index.ts).
import { Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { Chip, Input } from '@/components/ui';
import type { AppRoleOption } from '@/api/console';

export interface AddRoleComboboxProps {
    appName: string;
    /** Options come from `GET /admin/app-roles?application=` — only what the
     * app currently declares, so a link can never point at a role that does
     * not exist (frame "43 Add role combobox"). */
    options: AppRoleOption[];
    loading: boolean;
    /** app_role keys already linked for THIS role row — rendered checked+disabled. */
    linkedAppRoles: string[];
    onSelect: (appRole: string) => void;
    disabled?: boolean;
    triggerLabel?: string;
}

/**
 * EAA-1817: the "+ vai trò" combobox — frame "43 Add role combobox". No free
 * text: the search box only filters the app's own synced catalog. Esc closes
 * without writing; Enter/Space on the trigger opens; arrow keys move through
 * the list.
 */
export function AddRoleCombobox({
    appName,
    options,
    loading,
    linkedAppRoles,
    onSelect,
    disabled,
    triggerLabel,
}: AddRoleComboboxProps) {
    const { t } = useTranslation();
    // EAA-1845: default moved from the destructure default into the body so
    // it can go through `t()` — a hook cannot be called in a parameter
    // initializer.
    const resolvedTriggerLabel =
        triggerLabel ?? t('console.roleMap.addRoleCombobox.defaultTriggerLabel');
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const linkedSet = useMemo(() => new Set(linkedAppRoles), [linkedAppRoles]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
            (o) => o.key.toLowerCase().includes(q) || o.name.toLowerCase().includes(q)
        );
    }, [options, query]);

    function reset() {
        setQuery('');
        setActiveIndex(0);
    }

    function handleOpenChange(next: boolean) {
        setOpen(next);
        if (next) {
            reset();
            window.setTimeout(() => inputRef.current?.focus(), 0);
        }
    }

    function commit(option: AppRoleOption) {
        if (linkedSet.has(option.key)) return;
        onSelect(option.key);
        setOpen(false);
    }

    function onKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Escape') {
            // EAA-1817: Esc closes without writing.
            event.preventDefault();
            setOpen(false);
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const option = filtered[activeIndex];
            if (option) commit(option);
        }
    }

    const content = (
        <div className="rm-combobox" onKeyDown={onKeyDown}>
            <label className="rm-combobox__search">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                </svg>
                {/* EAA-1817: kit `Input` — its ref is typed `HTMLInputElement` (same
                    shape this component's `useRef<HTMLInputElement>` already expects),
                    so the focus-on-open call below is unchanged. */}
                <Input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActiveIndex(0);
                    }}
                    placeholder={t('console.roleMap.addRoleCombobox.searchPlaceholder')}
                    aria-label={t('console.roleMap.addRoleCombobox.searchAriaLabel')}
                />
            </label>
            <div className="rm-combobox__group-label">
                {loading
                    ? t('console.roleMap.addRoleCombobox.loadingCatalog')
                    : t('console.roleMap.addRoleCombobox.catalogCount', {
                          count: options.length,
                          appName,
                      })}
            </div>
            <div className="rm-combobox__list" role="listbox">
                {!loading && filtered.length === 0 && (
                    <div className="rm-combobox__empty">
                        {t('console.roleMap.addRoleCombobox.emptyResult')}
                    </div>
                )}
                {filtered.map((option, index) => {
                    const checked = linkedSet.has(option.key);
                    return (
                        <button
                            type="button"
                            key={option.key}
                            role="option"
                            aria-selected={checked}
                            className={
                                'rm-combobox__option' +
                                (checked ? ' is-checked' : '') +
                                (index === activeIndex ? ' is-active' : '')
                            }
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => commit(option)}
                            disabled={checked}
                        >
                            <span className={'rm-combobox__checkbox' + (checked ? ' is-checked' : '')} />
                            <code className="rm-combobox__option-key">{option.key}</code>
                            <span className="rm-combobox__option-name">
                                {checked
                                    ? t('console.roleMap.addRoleCombobox.checkedLabel')
                                    : option.name || '—'}
                            </span>
                        </button>
                    );
                })}
            </div>
            <div className="rm-combobox__divider" />
            <p className="rm-combobox__hint">
                {t('console.roleMap.addRoleCombobox.hint', { appName })}
            </p>
        </div>
    );

    if (disabled) {
        return <Chip tone="add">{resolvedTriggerLabel}</Chip>;
    }

    return (
        // EAA-1817: keep antd's built-in `trigger="click"` (mouse click open/
        // close/outside-close all come free that way) but give `Chip` itself
        // no `onClick` — wiring one there too would double-toggle on a real
        // click, since rc-trigger already attaches its own listener to this
        // child regardless of whether it has its own handler. The outer span
        // is what adds Enter/Space (Chip's own keyboard handling only fires
        // for elements that already have `onClick`, and this one intentionally
        // doesn't).
        <Popover
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            content={content}
            placement="bottomLeft"
            overlayInnerStyle={{ padding: 0 }}
        >
            <span
                tabIndex={0}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                style={{ display: 'inline-flex' }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenChange(true);
                    }
                }}
            >
                <Chip tone="add">{resolvedTriggerLabel}</Chip>
            </span>
        </Popover>
    );
}
